// Shared "Add to device calendar" helper. Builds either a Google Calendar
// event-creation URL (desktop) or downloads an .ics file (mobile, fallback).
// Mirrors the logic already in LeadDetail.jsx so the Tasklio Calendar
// detail dialog can re-use it without duplicating the .ics builder.

const esc = (v) =>
  String(v ?? "-")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const toYMD = (d) => d.toISOString().slice(0, 10).replace(/-/g, "");
const toICSStamp = (d) => {
  // YYYYMMDDTHHmmssZ in UTC
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
};

export const addToDeviceCalendar = ({
  title = "Cleaning job",
  start,
  durationMinutes = 120,
  description = "",
  location = "",
  uid,
} = {}) => {
  const startDate = start instanceof Date ? start : new Date(start || Date.now());
  if (isNaN(startDate.getTime())) {
    // Last-ditch fallback — open a blank Google Calendar event-creation.
    window.open("https://calendar.google.com/calendar/u/0/r/eventedit", "_blank", "noopener,noreferrer");
    return;
  }
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

  const ua = navigator.userAgent || "";
  const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);

  const downloadIcs = () => {
    const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Tasklio AI//Calendar//EN
BEGIN:VEVENT
UID:${uid || Date.now()}@tasklio
DTSTAMP:${toICSStamp(new Date())}
DTSTART:${toICSStamp(startDate)}
DTEND:${toICSStamp(endDate)}
SUMMARY:${esc(title)}
LOCATION:${esc(location)}
DESCRIPTION:${esc(description)}
END:VEVENT
END:VCALENDAR`;
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tasklio-job.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isMobile) {
    try {
      const params = new URLSearchParams({
        action: "TEMPLATE",
        text: title,
        // Google Calendar timed format: YYYYMMDDTHHmmssZ/YYYYMMDDTHHmmssZ
        dates: `${toICSStamp(startDate)}/${toICSStamp(endDate)}`,
        details: description,
        location,
      });
      const url = `https://calendar.google.com/calendar/render?${params.toString()}`;
      const popup = window.open(url, "_blank", "noopener,noreferrer");
      if (popup) return;
    } catch {
      // fall through to .ics
    }
  }

  try {
    downloadIcs();
  } catch {
    window.open("https://calendar.google.com/calendar/u/0/r/eventedit", "_blank", "noopener,noreferrer");
  }
};

// Eslint-friendly: silence unused-export warnings for helpers we may
// later expose. Keep only the public symbol exported.
void toYMD;
